import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import {browserHistory} from 'react-router'
import styles from './Rides.scss'
import cardStyles from 'common/SearchableCard/SearchableCard.scss'
import {SearchableCardMulti, SearchableCardInput, SearchableCardLocationInput} from 'common/SearchableCardMulti'
import {Card} from 'common/styleguide/Card'
import {Loadable} from 'common/Loadable'
import {apiv3} from 'lib/apiv3'
import _ from 'lodash'
import {findAsYouType} from 'lib/findAsYouType'
import {EmptyRides} from './EmptyRides'
import {PopoverFilter, FilterButton} from 'common/PopoverFilter'
import {OrderedMap, Map, Set} from 'immutable'
import {withMetricUnits} from 'common/units/MetricUnitsContext'
import * as Filters from '../shared/filters'
import {Chip} from 'common/styleguide/Chip'
import {IntroWidget} from 'common/IntroWidget'
import {TRouteAccordian, TRouteListItemExpandable} from '../shared/TRouteAccordian'
import {RideCondensedStats} from './RideCondensedStats'
import {LoadableExpandedRidePreview} from './ExpandedRidePreview'
import {currentUserHasPrivilege} from 'lib/utils'
import * as userActions from 'lib/api/currentUser'
import * as tripsActions from 'lib/api/trips'
import {Trip} from 'data/records'
import {tripSelectors as tripResourceSelectors} from 'data/resources/trips'
import {collectionActions} from 'data/resources/collections'
import {collectionItemActions} from 'data/resources/collectionItems'
import {CTA} from 'common/styleguide/CTA'
import {CircleSpinner} from 'common/spinners/CircleSpinner'
import {PopoverMenuItem, PopoverMenuHeading} from 'common/styleguide/PopoverMenu'
import {RadioGroup} from 'common/styleguide/RadioGroup'
import {Icon} from 'common/Icon'
import popoverMenuStyles from 'common/styleguide/PopoverMenu/PopoverMenu.scss'
import {DEFAULT_OFFSET, DEFAULT_PER_PAGE, MAX_PER_PAGE} from 'lib/loadAllPages'
import {ColWrap, MainCol} from 'common/layouts/SideNavLayout/TwoCol'
import Helmet from 'react-helmet'
import {IeNudgeBar} from 'common/IeNudgeBar'
import {withSelect} from 'common/WithSelectable'
import {RowCheckbox} from '../shared/RowCheckbox'
import {MobileCollapse} from 'common/layouts/SideNavLayout/SideNavContexts'
import {DeleteTRoutes, DELETE_TROUTES_DIALOG} from 'common/dialogs/DeleteTRoute'
import cx from 'classnames'
import {spoofV3ResourceExtras} from 'data/helpers/spoofActionCreator'

const sortBySearchOpts = {
  date: 'departed_or_created',
  name: 'name',
  distance: 'length',
  eleGain: 'elevation',
  watts: 'avg_watts',
  avgSpeed: 'avg_speed'
}

const sortByMenuOpts = OrderedMap({
  date: 'Date',
  name: 'Name',
  distance: 'Distance',
  eleGain: 'Elevation Gain',
  watts: 'Average Watts',
  avgSpeed: 'Average speed'
})

const sortDirMenuOpts = OrderedMap({
  'ASC': 'Ascending',
  'DESC': 'Descending'
})

const imperialRadiusMenuOpts = OrderedMap({
  '1': '1 mile',
  '5': '5 miles',
  '10': '10 miles',
  '25': '25 miles',
  '50': '50 miles',
  '100': '100 miles',
  '250': '250 miles'
})

const metricRadiusMenuOpts = OrderedMap({
  '5': '5 kilometers',
  '10': '10 kilometers',
  '25': '25 kilometers',
  '50': '50 kilometers',
  '100': '100 kilometers',
  '250': '250 kilometers',
  '500': '500 kilometers'
})

const startPageOptions = {
  offset: DEFAULT_OFFSET,
  limit: DEFAULT_PER_PAGE,
  loadMoreClicked: false
}

class _Rides extends React.PureComponent {
  constructor (props) {
    super(props)
    const {loadableResources: initialResults} = props

    this.state = Object.assign({
      searchParams: {
        searchKeywords: '',
        locationKeywords: '',
        sortBy: 'date',
        sortDir: 'DESC',
        radius: '10',
        filters: Map()
      },
      initialResults,
      search: initialResults,
      searchPending: false,
      selectAllChecked: false,
      dialog: null
    }, startPageOptions)
    this._findAsYouType = findAsYouType({
      searchFn: this.executeSearch,
      onResults: this.onResults,
      throttle: 0
    })
    this._debouncedFindAsYouType = _.debounce(this._findAsYouType, 350)
  }

  componentDidMount () {
    const historyState = browserHistory.getCurrentLocation().state
    if (historyState) {
      const searchParams = {...historyState}
      searchParams.filters = Map(searchParams.filters)
      this.newSearch(searchParams, this._findAsYouType)
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const {search, selectAllChecked, searchPending} = this.state
    const {results} = search

    const aSearchJustCompleted = prevState.searchPending && !searchPending
    if (!prevState.selectAllChecked && selectAllChecked && !aSearchJustCompleted) {
      this.props.selectItems(results.map((t) => t.id))
    }
    if (prevState.selectAllChecked && !selectAllChecked && !aSearchJustCompleted) {
      this.props.clearSelection()
    }
  }

  newSearch = (newSearchParams, callback) => {
    const searchParams = {...this.state.searchParams, ...newSearchParams}
    this.setState(
      Object.assign({}, startPageOptions, {searchParams}, {searchPending: true, selectAllChecked: false}),
      () => {
        this.props.clearSelection()
        callback()
      }
    )
  }

  onSearchKeywordChange = (e) => {
    const searchKeywords = e.target.value
    this.newSearch({searchKeywords}, this._debouncedFindAsYouType)
  }

  onLocationKeywordChange = (e) => {
    const locationKeywords = e.target.value
    this.newSearch({locationKeywords}, this._debouncedFindAsYouType)
  }

  onSortByChange = (e) => {
    this.newSearch({sortBy: e}, this._findAsYouType)
  }

  onSortDirChange = (sortDir) => {
    this.newSearch({sortDir}, this._findAsYouType)
  }

  toggleSelectAll = () => {
    this.setState((state) => ({selectAllChecked: !state.selectAllChecked}))
  }

  onRadiusChange = (radius) => (this.newSearch({radius}, this._findAsYouType))

  executeSearch = (extraCriteria = {}) => {
    const {currentUser} = this.props
    const {
      searchParams: {
        searchKeywords,
        locationKeywords,
        sortBy,
        sortDir,
        radius,
        filters
      },
      offset,
      limit
    } = this.state

    // note - the search endpoint does unit conversion automatically, on
    // the backend, based off the user's metric_units preference. There
    // isn't any way to override this, we just have to make sure that
    // we display 'km' and 'meter' labels for users that have metricUnits=t
    // and imperial labels for users that don't, and then hand off the
    // user input untouched to the search function
    return apiv3.trips.search({
      keywords: searchKeywords,
      offset: offset,
      limit: limit,
      userId: currentUser.id,
      sortBy: `${sortBySearchOpts[sortBy]} ${sortDir}`,
      startLocation: locationKeywords,
      radius: radius,
      ...filters.toJS(),
      ...extraCriteria
    })
  }

  isDefaultSearch = () => {
    const {searchParams} = this.state
    return (!searchParams.searchKeywords) &&
    (!searchParams.locationKeywords) &&
    (searchParams.filters.size === 0) &&
    (searchParams.sortBy === 'date') &&
    (searchParams.sortDir === 'DESC') &&
    (searchParams.radius === '10') &&
    (this.state.offset === startPageOptions['offset']) &&
    (this.state.limit === startPageOptions['limit'])
  }

  onResults = (nextSearch) => {
    const {spoofTrips} = this.props
    const {searchParams} = this.state

    spoofTrips(nextSearch.rawResults)
    browserHistory.replace({...browserHistory.getCurrentLocation(), state: {...searchParams, filters: searchParams.filters.toJS()}})

    if (nextSearch.params.mergeResults) {
      this.setState((prevState) => {
        const combinedResults = _.concat(prevState.search.results, nextSearch.results)
        return {
          search: Object.assign({}, prevState.search, {results: combinedResults}),
          searchPending: false,
          selectAllChecked: false
        }
      })
    } else {
      this.setState({
        search: nextSearch,
        searchPending: false,
        selectAllChecked: false
      })
    }
  }

  setFilter = (filterKey, filterValue) => {
    this.newSearch({
      filters: this.state.searchParams.filters.set(filterKey, filterValue)
    }, this._findAsYouType)
  }

  clearFilter = (filterKey) => {
    this.newSearch({
      filters: this.state.searchParams.filters.remove(filterKey)
    }, this._findAsYouType)
  }

  getSelectedItems = () => {
    const {selectedItems} = this.props
    const {search} = this.state
    const {results} = search

    // Just to be double-sure that only items currently displayed can be selected
    return results.filter((t) => selectedItems.has(t.id))
  }

  setPrivacy = (privacy) => {
    const {clearSelection, batchSetVisibility} = this.props
    const {search} = this.state
    const {results} = search

    const selectedIds = Set(this.getSelectedItems().map(t => t.id))

    batchSetVisibility(selectedIds, privacy).then(() => {
      // I'm just going to update the values in local state until we do the full on reducer thing later
      const newResults = results.map((t) => {
        if (selectedIds.has(t.id)) {
          return t.set('visibility', parseInt(privacy, 10))
        }
        return t
      })
      this.setState({search: Object.assign({}, search, {results: newResults})})

      clearSelection()
    })
  }

  addToCollections = (collectionIds) => {
    const {createCollectionItem} = this.props
    const selectedIds = this.getSelectedItems().map(t => t.id)

    collectionIds.map(cid => {
      selectedIds.forEach(rid => createCollectionItem({collectionId: cid, itemId: rid, itemType: 'Trip'}))
    })
  }

  deleteRides = () => {
    const {clearSelection, batchDelete} = this.props
    const {search} = this.state
    const {results} = search

    const selectedIds = Set(this.getSelectedItems().map(t => t.id))

    return batchDelete(selectedIds).then(() => {
      const newResults = results.filter((t) => !selectedIds.has(t.id))

      this.setState({search: Object.assign({}, search, {results: newResults}), dialog: null})

      clearSelection()
    })
  }

  toggleSort = () => {
    this.setState({showSort: !this.state.showSort})
  }

  loadMore = () => {
    const {loadMoreClicked} = this.state
    this.setState((prevState) => ({
      searchPending: true,
      offset: prevState.offset + prevState.limit,
      limit: MAX_PER_PAGE
    }), () => this._findAsYouType({mergeResults: !!loadMoreClicked}))

    // merge results next time we click loadMore
    this.setState({loadMoreClicked: true})
  }

  showDialog = (dialog) => {
    this.setState({dialog})
  }

  hideDialog = () => {
    this.setState({dialog: null})
  }

  render () {
    const {
      currentUser,
      setPreference,
      selectedItems,
      selectItem,
      deselectItem,
      findTrip
    } = this.props
    const {
      search,
      initialResults,
      searchParams: {
        searchKeywords,
        locationKeywords,
        sortBy,
        sortDir,
        radius,
        filters
      },
      offset,
      limit,
      searchPending,
      selectAllChecked,
      dialog
    } = this.state
    const {results} = search

    if (search === initialResults && results.length === 0) {
      return <EmptyRides />
    }

    const showSelects = currentUserHasPrivilege('activity_center')
    const showSelectionToolbar = selectedItems.size > 0

    return (
      <Fragment>
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        <ColWrap className={styles.earlyCollapse}>
          <Helmet title='Rides · Ride with GPS' />
          <MainCol style={{marginRight: 0}}>
            <h2 data-heading-alt>Rides</h2>
            <div className={styles.filters}>
              {showSelects && <MobileCollapse><FilterButton>
                <input type='checkbox' onChange={this.toggleSelectAll} checked={selectAllChecked} />
              </FilterButton></MobileCollapse>}
              {showSelectionToolbar && <Fragment>
                <PopoverFilter label='Set Privacy'>
                  {({toggleExpand}) => <Filters.SetPrivacyMenu
                    onApply={(privacy) => { toggleExpand(); this.setPrivacy(privacy) }} />}
                </PopoverFilter>
                {currentUser.accountLevel === 11 && <PopoverFilter label='Add to Collection'>
                  {({toggleExpand}) => <Filters.AddToCollectionMenu
                    userId={currentUser.id}
                    toggleExpand={() => toggleExpand()}
                    troutes={this.getSelectedItems()}
                    objectType='Trip'
                    onApply={(collections) => { toggleExpand(); this.addToCollections(collections) }} />}
                </PopoverFilter>}
                <FilterButton onClick={() => this.showDialog(DELETE_TROUTES_DIALOG)} valignCenter>
                  <Icon i='md-trash' style={{color: '#757575'}} size={20} /> Delete
                </FilterButton>
              </Fragment>}
              {!showSelectionToolbar && <Fragment>
                <PopoverFilter label='Distance'>
                  {({toggleExpand}) => <Filters.LengthInput
                    defaultValue={filters.get('length')}
                    onApply={length => { this.setFilter('length', length) }}
                    onClear={() => { toggleExpand(); this.clearFilter('length') }} />}
                </PopoverFilter>
                <PopoverFilter label='Elevation Gain'>
                  {({toggleExpand}) => <Filters.ElevationInput
                    defaultValue={filters.get('elevation')}
                    onApply={elevation => { this.setFilter('elevation', elevation) }}
                    onClear={() => { toggleExpand(); this.clearFilter('elevation') }} />}
                </PopoverFilter>
                <PopoverFilter label='Moving Time'>
                  {({toggleExpand}) => <Filters.DurationInput
                    defaultValue={filters.get('movingTime')}
                    onApply={movingTime => { this.setFilter('movingTime', movingTime) }}
                    onClear={() => { toggleExpand(); this.clearFilter('movingTime') }} />}
                </PopoverFilter>
                <PopoverFilter label='Avg Speed' innerLabel='Average Speed'>
                  {({toggleExpand}) => <Filters.SpeedInput
                    defaultValue={filters.get('avgSpeed')}
                    onApply={avgSpeed => { this.setFilter('avgSpeed', avgSpeed) }}
                    onClear={() => { toggleExpand(); this.clearFilter('avgSpeed') }} />}
                </PopoverFilter>
                <PopoverFilter label='Avg Watts' innerLabel='Average Watts'>
                  {({toggleExpand}) => <Filters.WattsInput
                    defaultValue={filters.get('avgWatts')}
                    onApply={avgWatts => { this.setFilter('avgWatts', avgWatts) }}
                    onClear={() => { toggleExpand(); this.clearFilter('avgWatts') }} />}
                </PopoverFilter>
                <PopoverFilter label='Ride Date'>
                  {({toggleExpand}) => <Filters.DateInput
                    currentUser={currentUser}
                    value={filters.get('rideDate')}
                    defaultValue={filters.get('rideDate')}
                    onApply={rideDate => { this.setFilter('rideDate', rideDate) }}
                    onClear={() => { toggleExpand(); this.clearFilter('rideDate') }} />}
                </PopoverFilter>
              </Fragment>}
              <PopoverFilter
                label={`Sort By: ${sortByMenuOpts.get(sortBy)}`}
                hidePopoverLabel
                noPadding
                className={cx(styles.filterRight, styles.specificityHack)}
                icon={<Icon i={`mdi-sort-${sortDir === 'ASC' ? 'ascending' : 'descending'}`} />}>
                <ul className={popoverMenuStyles.PopoverMenu}>
                  <PopoverMenuHeading>Sort Options</PopoverMenuHeading>
                  <PopoverMenuItem>
                    <RadioGroup
                      style={{margin: '0 0'}}
                      horizontal
                      options={sortByMenuOpts}
                      selected={sortBy}
                      radioClass={styles.radioClass}
                      onChange={this.onSortByChange} />
                  </PopoverMenuItem>
                  <PopoverMenuHeading>Sort Order</PopoverMenuHeading>
                  <PopoverMenuItem>
                    <RadioGroup
                      style={{margin: '0 0'}}
                      horizontal
                      options={sortDirMenuOpts}
                      selected={sortDir}
                      radioClass={styles.radioClass}
                      onChange={this.onSortDirChange} />
                  </PopoverMenuItem>
                </ul>
              </PopoverFilter>
            </div>

            <Card noPadding>
              <SearchableCardMulti
                inputs={[
                  <SearchableCardInput
                    key='keyword'
                    placeholder='Keyword'
                    onKeywordChange={this.onSearchKeywordChange}
                    keywords={searchKeywords} />,
                  <UnitAwareSearchableCardLocationInput
                    key='location'
                    placeholder='Start location (city, state)'
                    onKeywordChange={this.onLocationKeywordChange}
                    keywords={locationKeywords}
                    radius={radius}
                    onRadiusChange={this.onRadiusChange} />
                ]}
                resultsLength={results.length}
                resultsCount={!this.isDefaultSearch() ? search.results_count : undefined}
                chips={filters.size > 0 && filters.map((v, k) => <FilterCard key={k} type={k} value={v} onDismiss={() => this.clearFilter(k)} />).valueSeq()}>
                <Results
                  results={results}
                  executeSearch={this.executeSearch}
                  currentUser={currentUser}
                  onResults={this.onResults}
                  showSelects={showSelects}
                  selectedItems={selectedItems}
                  selectItem={selectItem}
                  deselectItem={deselectItem}
                  findTrip={findTrip}
                  accordianProps={{
                    deleteTRoute: this.props.deleteTrip,
                    currentUser: currentUser
                  }}
                />
                {search.results_count > results.length && search.results_count > limit &&
                <div className={cardStyles.resultsFooter}>
                  <CTA inline secondary margins={false} disabled={searchPending} onClick={() => !searchPending && this.loadMore()}>
                    {searchPending && <CircleSpinner size={14} />} Load more
                  </CTA>
                  <div style={{paddingTop: '20px'}}>
                    <span className={cardStyles.footerText}>Showing 1 - {results.length} of {search.results_count} results</span>
                  </div>
                </div>}
                {!searchPending &&
                search.results_count > startPageOptions.limit &&
                (offset + limit) >= search.results_count &&
                results.length === search.results_count &&
                <div className={cardStyles.resultsFooter}>
                  <span className={cardStyles.footerText}>No more items to display</span>
                </div>}
                {results.length === 0 && <IntroWidget large i='md-bicycle'>
                We couldn't find any rides that matched your search
                </IntroWidget>}
              </SearchableCardMulti>
            </Card>
          </MainCol>
        </ColWrap>
        {dialog === DELETE_TROUTES_DIALOG &&
          <DeleteTRoutes
            troutes={this.getSelectedItems()}
            onConfirm={this.deleteRides}
            onRequestClose={this.hideDialog} />}
      </Fragment>
    )
  }
}

class Results extends React.Component {
  shouldComponentUpdate (nextProps) {
    return nextProps.results !== this.props.results || nextProps.selectedItems !== this.props.selectedItems
  }

  render () {
    const {
      results,
      executeSearch,
      currentUser,
      onResults,
      showSelects,
      selectedItems,
      selectItem,
      deselectItem,
      accordianProps,
      findTrip
    } = this.props

    const ids = results.map((r) => r.id)

    return (
      <TRouteAccordian {...accordianProps}>
        {ids.map(id => {
          const trip = findTrip(id)
          return trip && <TRouteListItemExpandable
            condensedComponent={RideCondensedStats}
            previewComponent={LoadableExpandedRidePreview}
            key={id}
            troute={trip}
            expandKey={id}
            executeSearch={executeSearch}
            currentUser={currentUser}
            onResults={onResults}
            checkbox={showSelects &&
              <RowCheckbox
                onChange={() => selectedItems.has(id)
                  ? deselectItem(id)
                  : selectItem(id)}
                checked={selectedItems.has(id)} />
            } />
        }).filter(x => x)}
      </TRouteAccordian>
    )
  }
}

const UnitAwareSearchableCardLocationInput = withMetricUnits(({metricUnits, ...props}) => (
  <SearchableCardLocationInput
    radiusMenuOpts={metricUnits ? metricRadiusMenuOpts : imperialRadiusMenuOpts}
    {...props} />
))

const FilterCard = withMetricUnits(({type, value, metricUnits, onDismiss}) => {
  const distanceUnits = metricUnits ? 'km' : 'miles'
  const eleUnits = metricUnits ? 'm' : 'ft'
  const speedUnits = metricUnits ? 'kph' : 'mph'

  switch (type) {
    case 'length':
      return <Chip onDismiss={onDismiss}>distance: {Filters.formatRangeLabel({...value, unitLabel: distanceUnits, metricType: 'distance'})}</Chip>
    case 'elevation':
      return <Chip onDismiss={onDismiss}>elevation: {Filters.formatRangeLabel({...value, unitLabel: eleUnits, metricType: 'distance'})}</Chip>
    case 'movingTime':
      return <Chip onDismiss={onDismiss}>moving time: {Filters.formatRangeLabel({...value, unitLabel: 'hrs', metricType: 'duration'})}</Chip>
    case 'avgSpeed':
      return <Chip onDismiss={onDismiss}>average speed: {Filters.formatRangeLabel({...value, unitLabel: speedUnits, metricType: 'speed'})}</Chip>
    case 'avgWatts':
      return <Chip onDismiss={onDismiss}>average watts: {Filters.formatRangeLabel({...value, unitLabel: 'watts', metricType: 'power'})}</Chip>
    case 'rideDate':
      return <Chip onDismiss={onDismiss}>ride date: {Filters.formatDateRangeLabel(value.min, value.max)}</Chip>
    default:
      console.warn(`encountered unknown filter type ${type}`)
      return null
  }
})

const doInitialSearch = (currentUser) => (
  apiv3.trips.search({
    limit: 50,
    userId: currentUser.id,
    sortBy: 'departed_or_created DESC'
  })
)

export const Rides = withSelect(_Rides)

const LoadableRides = (props) => {
  return <Loadable
    {...props}
    component={Rides}
    loadingText='Loading your ride history'
    resourceLoaded={() => (
      doInitialSearch(props.currentUser)
        .then((resp) => {
          props.spoofTrips(resp.rawResults)
          return resp
        })
    )} />
}

const mapDispatchToProps = (dispatch) => {
  const actionCreators = {
    deleteTrip: tripsActions.deleteTrip,
    batchSetVisibility: tripsActions.batchSetVisibility,
    batchDelete: tripsActions.batchDelete,
    setPreference: userActions.setPreference,
    loadCollections: collectionActions.list,
    createCollectionItem: (attrs) => collectionItemActions.create({collectionItem: attrs})
  }

  return {
    ...bindActionCreators(actionCreators, dispatch),
    spoofTrips: (extras) => dispatch(spoofV3ResourceExtras(extras, [Trip]))
  }
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.currentUser,
    findTrip: (id) => {
      return tripResourceSelectors.find(state, id)
    }
  }
}

export const RidesContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableRides)
